import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClientError } from "@victor/api-client";
import type { TenantModuleCode } from "@victor/types";
import { api } from "../../../lib/api";
import { Button } from "../../../ui/Button";
import { Select } from "../../../ui/Select";
import { Input } from "../../../ui/Input";
import { Modal } from "../../../ui/Modal";
import { PageSpinner } from "../../../ui/Spinner";
import {
  Badge,
  EmptyInline,
  Field,
  Panel,
  SectionTitle,
  StatTile,
  fmtDate,
  fmtMoney,
} from "./_ui";

type Props = { tenantId: string };

export function LicenseTab({ tenantId }: Props) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const licQ = useQuery({
    queryKey: ["tenant-license", tenantId],
    queryFn: () => api.license.get(tenantId),
  });

  const revokeMut = useMutation({
    mutationFn: (moduleCode: string) =>
      api.license.revokeModule(tenantId, moduleCode),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["tenant-license", tenantId] }),
  });

  if (licQ.isLoading) return <PageSpinner />;
  const lic = licQ.data;
  if (!lic) {
    return (
      <Panel>
        <EmptyInline>License indisponible.</EmptyInline>
      </Panel>
    );
  }

  const availableModules = lic.catalog.filter(
    (c) => !lic.modules.some((m) => m.moduleCode === c.code),
  );

  return (
    <div className="space-y-5">
      {/* ---- KPIs plan ---- */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={ICON.badge}
          tone="accent"
          label="Plan"
          value={lic.plan.toUpperCase()}
        />
        <StatTile
          icon={ICON.pulse}
          tone="navy"
          label="Statut abo"
          value={
            <Badge
              tone={lic.statut === "actif" ? "emerald" : "amber"}
              dot
            >
              {lic.statut}
            </Badge>
          }
        />
        <StatTile
          icon={ICON.refresh}
          tone="navy"
          label="Cycle"
          value={lic.cycle ?? "—"}
        />
        <StatTile
          icon={ICON.euro}
          tone="emerald"
          label="Base"
          value={lic.basePrix != null ? `${fmtMoney(lic.basePrix)} €` : "—"}
        />
      </div>

      {/* ---- Quotas & échéances ---- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel>
          <SectionTitle icon={ICON.gauge}>Quotas</SectionTitle>
          <div className="divide-y divide-navy-100">
            <Field label="Max agents">{lic.quotas.maxAgents ?? "—"}</Field>
            <Field label="Max admins">{lic.quotas.maxAdmins ?? "—"}</Field>
            <Field label="Max sites">{lic.quotas.maxSites ?? "—"}</Field>
          </div>
        </Panel>

        <Panel>
          <SectionTitle icon={ICON.calendar}>Échéances</SectionTitle>
          <div className="divide-y divide-navy-100">
            <Field label="Fin essai">
              {lic.finEssai ? fmtDate(lic.finEssai) : "—"}
            </Field>
            <Field label="Fin période en cours">
              {lic.currentPeriodEnd ? fmtDate(lic.currentPeriodEnd) : "—"}
            </Field>
          </div>
        </Panel>
      </div>

      {/* ---- Modules ---- */}
      <Panel>
        <SectionTitle
          icon={ICON.modules}
          action={
            <Button
              onClick={() => setAddOpen(true)}
              disabled={availableModules.length === 0}
            >
              + Activer un module
            </Button>
          }
        >
          Modules activés
        </SectionTitle>

        {lic.modules.length === 0 ? (
          <EmptyInline>Aucun module supplémentaire activé.</EmptyInline>
        ) : (
          <div className="space-y-2">
            {lic.modules.map((m) => {
              const meta = lic.catalog.find((c) => c.code === m.moduleCode);
              return (
                <div
                  key={m.id}
                  className="flex items-center justify-between gap-3 rounded-xl bg-navy-50/40 px-4 py-3 ring-1 ring-navy-100 transition-colors hover:bg-navy-50/70"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-navy-800">
                        {meta?.libelle ?? m.moduleCode}
                      </p>
                      {m.expiresAt ? (
                        <Badge tone="amber">expire {fmtDate(m.expiresAt)}</Badge>
                      ) : (
                        <Badge tone="emerald" dot>
                          sans expiration
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-[11px] text-navy-500">
                      activé le {fmtDate(m.activatedAt)}
                    </p>
                    <p className="mt-1 font-mono text-[10px] text-navy-400 break-all">
                      {m.activationKey}
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (window.confirm(`Révoquer le module ${meta?.libelle ?? m.moduleCode} ?`)) {
                        revokeMut.mutate(m.moduleCode);
                      }
                    }}
                    disabled={revokeMut.isPending}
                  >
                    Révoquer
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {addOpen ? (
        <AddModuleModal
          tenantId={tenantId}
          available={availableModules.map((c) => ({
            value: c.code,
            label: c.libelle,
            desc: c.description,
          }))}
          onClose={() => setAddOpen(false)}
        />
      ) : null}
    </div>
  );
}

function AddModuleModal({
  tenantId,
  available,
  onClose,
}: {
  tenantId: string;
  available: Array<{ value: string; label: string; desc: string }>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [moduleCode, setModuleCode] = useState(available[0]?.value ?? "");
  const [expiresAt, setExpiresAt] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api.license.addModule(tenantId, {
        moduleCode: moduleCode as TenantModuleCode,
        expiresAt: expiresAt || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-license", tenantId] });
      onClose();
    },
  });

  const selected = available.find((a) => a.value === moduleCode);

  return (
    <Modal open onClose={onClose} title="Activer un module">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="space-y-4"
      >
        <Select
          label="Module"
          value={moduleCode}
          onChange={(e) => setModuleCode(e.target.value)}
          options={available.map((a) => ({ value: a.value, label: a.label }))}
        />
        {selected ? (
          <p className="text-xs text-navy-500">{selected.desc}</p>
        ) : null}
        <Input
          type="date"
          label="Date d'expiration (optionnel)"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />
        <p className="text-xs text-navy-500">
          Une clé d'activation sera générée. Elle reste consultable depuis cet
          onglet et tracée en audit.
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t border-navy-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? "Activation…" : "Activer"}
          </Button>
        </div>
        {mut.error ? (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {mut.error instanceof ApiClientError ? mut.error.message : "Erreur."}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  badge: (
    <svg {...sp}><circle cx="12" cy="8" r="6" /><path d="M15.5 13 17 22l-5-3-5 3 1.5-9" /></svg>
  ),
  pulse: (
    <svg {...sp}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
  ),
  refresh: (
    <svg {...sp}><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>
  ),
  euro: (
    <svg {...sp}><path d="M4 10h12" /><path d="M4 14h9" /><path d="M19 6a7.5 7.5 0 1 0 0 12" /></svg>
  ),
  gauge: (
    <svg {...sp}><path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" /></svg>
  ),
  calendar: (
    <svg {...sp}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
  ),
  modules: (
    <svg {...sp}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg>
  ),
};
